<template>
    <div>
        <h2 class="clickable" :class="{'collapsed-heading': !expanded}" @click="expanded=!expanded">
            Configurations
            <v-icon v-if="expanded">arrow_drop_up</v-icon>
            <v-icon v-else>arrow_drop_down</v-icon>
        </h2>
        <v-expand-transition>
            <v-card v-show="expanded">
                <v-container fluid>
                    <v-layout row wrap>
                      <div v-if="!unit.configurations || unit.configurations.length === 0">No unit configurations found</div>
                      <v-flex md12 v-for="(configuration, i) in unit.configurations" :key="configuration.id">
                        <v-autocomplete
                          v-model="unit.configurations[i]"
                          :label="unit.configurations[i].type"
                          :items="getItems(configuration.type)"
                          item-value="id"
                          item-text="name"
                          return-object
                          :readonly="unit.isDeleted || !userIsFleetManager"
                          autocomplete="off"
                        >
                          <v-icon slot="append-outer" v-if="!unit.isDeleted && userIsFleetManager" color="red" @click="removeConfiguration(unit.configurations[i])">delete</v-icon>
                        </v-autocomplete>
                      </v-flex>
                      <v-flex>
                          <v-menu v-model="firstdayofservicedialog" :close-on-content-click="false" max-width="290" :disabled="unit.isDeleted || !userIsFleetManager">
                              <template v-slot:activator="{ on }">
                                  <v-text-field
                                      v-model="formattedFirstDayOfService"
                                      label="First day of service"
                                      v-validate="'required'"
                                      :error-messages="errors.first('first day of service')"
                                      :readonly="unit.isDeleted || !userIsFleetManager"
                                      name="first day of service"
                                      v-on="on"
                                      append-icon="event"
                                      @click:append="firstdayofservicedialog = true"
                                  ></v-text-field>
                              </template>
                              <v-date-picker
                                  :min="minDate"
                                  :max="maxDate"
                                  :readonly="unit.isDeleted || !userIsFleetManager"
                                  no-title
                                  v-model="unit.firstDayOfService"
                                  @change="
                                      firstdayofservicedialog = false;
                                  "
                              ></v-date-picker>
                          </v-menu>
                      </v-flex>
                    </v-layout>
                </v-container>
                <v-card-actions v-if="!unit.isDeleted && userIsFleetManager">
                    <v-spacer></v-spacer>
                  <v-btn
                    color="primary"
                    @click="addConfiguration"
                    v-if="isVehicleUnit"
                  >Add New Configuration
                  </v-btn>
                  &nbsp;
                  <v-dialog v-model="dialog" max-width="500px" persistent>
                    <SelectConfigurationForm :configurations="this.availableConfigurations"
                                             :configurationTypes="this.availableConfigurationTypes"
                                             @add="handleUnitConfigurationAdded($event.configurationId)">
                      <template v-slot:header>
                        <v-spacer></v-spacer>
                        <v-btn @click="dialog = false" icon text>
                          <v-icon>close</v-icon>
                        </v-btn>
                      </template>
                      <template v-slot:actions>
                        <v-btn @click="dialog = false">Cancel</v-btn>
                      </template>
                    </SelectConfigurationForm>
                  </v-dialog>
                  <v-btn
                    color="primary"
                    @click="submit"
                  >Save
                  </v-btn>
                </v-card-actions>
            </v-card>
        </v-expand-transition>
    </div>
</template>

<script>
import moment from 'moment';
import { ConfigurationType, UnitType } from '@/models/enums';
import Configuration from '@/components/Configurations/ConfigurationsClient.vue';
import { Validator } from 'vee-validate';
import FormValidation from '@/components/FormValidation.vue';
import UnitsClient from '@/components/Units/UnitsClient';
import ConfigurationHelper from '@/lib/ConfigurationHelper';
import SelectConfigurationForm from '@/components/Configurations/SelectConfigurationForm';
import * as _ from 'lodash';
import { DateFormat } from '@/plugins/date-filter';

export default {
  components: { SelectConfigurationForm },
  mixins: [Configuration, FormValidation, UnitsClient],
    props: {
      unit: {
        required: true
      }
    },
    data: () => ({
      selected: null,
      dialog: false,
      expanded: true,
      configurations: [],
      firstdayofservicedialog: false,
      originalFirstDayOfService: null
    }),
    computed: {
      isVehicleUnit() {
        return this.unit.unitType === UnitType.Vehicle;
      },
      isContainerUnit() {
        return this.unit.unitType === UnitType.Container;
      },
      availableConfigurationTypes() {
        //Only allow the user to select a configuration type that is not yet configured.
        //Allow multiple Auxiliary, which can have multiple configurations.
        //Do not allow Brake configuration; this is configured elsewhere.
        let configurationTypes = Object.keys(ConfigurationType)
          .map(key => ConfigurationType[key])
          .filter(ct => ct !== ConfigurationType.Brake)
          .sort();
        return _.xorBy(configurationTypes,
          Array.from(this.unit.configurations, c => c.type)
            .filter(c => c !== ConfigurationType.Auxiliary));
      },
      availableConfigurations() {
        return _.xorBy(this.configurations, this.unit.configurations, 'id');
      },
      params() {
        return {
          orderBy: 'name'
        };
      },
      disableSubmit() {
        if (!this.fields) {
          return true;
        }
        return Object.keys(this.fields).some(key => this.fields[key].invalid);
      },
      bodyConfigurations() {
        return this.configurations.filter(c => c.type === ConfigurationType.Body);
      },
      bodyConfiguration() {
        return ConfigurationHelper.getBody(this.unit.configurations);
      },
      chassisConfigurations() {
        return this.configurations.filter(c => c.type === ConfigurationType.Chassis);
      },
      chassisConfiguration() {
        return ConfigurationHelper.getChassis(this.unit.configurations);
      },
      containerConfigurations() {
        return this.configurations.filter(c => c.type === ConfigurationType.Container);
      },
      minDate() {
        return moment('1900-01-01').format(DateFormat.Canonical);
      },
      maxDate() {
        return moment().add(4, 'M').format(DateFormat.Canonical);
      },
      formattedFirstDayOfService: {
        get() {
          return this.unit.firstDayOfService ? moment(this.unit.firstDayOfService).format(DateFormat.DefaultDisplay) : null;
        },
        set(newValue) {
          this.unit.firstDayOfService = newValue;
        }
      },
    },
    methods: {
      async handleUnitConfigurationAdded(newConfigurationId) {
        this.dialog = false;
        let newConfiguration = this.configurations.filter(c => c.id === newConfigurationId)[0];
        if (!newConfiguration) { return; }
        this.unit.configurations.push(newConfiguration);
      },
      getItems(configurationType) {
        return this.configurations.filter(c => c.type === configurationType);
      },
      addConfiguration() {
        this.dialog = true;
      },
      removeConfiguration(configuration) {
        const index = this.unit.configurations.indexOf(configuration);
        if (index > -1) {
          this.unit.configurations.splice(index, 1);
        }
      },
      async submitForm() {
        if (typeof this.originalFirstDayOfService !== 'undefined' && this.originalFirstDayOfService !== this.unit.firstDayOfService) {
          this.$confirmCancel.show(this.title(), this.message(), 'Save');
          this.$confirmCancel.$on('confirm-cancel', async confirmed => {
            if (!confirmed) {
                this.unit.firstDayOfService = this.originalFirstDayOfService;
            }

            await this.save();
          });
        } else {
          await this.save();
        }
      },
      async save() {
          this.$validator.validate().then(async success => {
            if (this.userIsFleetManager) {
              await this.updateUnit(this.unit, Array.from(this.unit.configurations, c => c.id));
            }
            await this.getConfigurations();
          });
      },
      title() {
        return `I sure hope you know what you're doing!`;
      },
      message(date) {
              return `<p>Editing the first day of service affects service predictions, contact Kris Shepherd prior to any alterations.</p>`;
      }
    },
    async mounted() {
      this.configurations = await this.getConfigurations();
      this.originalFirstDayOfService = this.unit.firstDayOfService;
    },
    watch: {
      unit: {
        handler() {
          this.unit.chassisConfigurationId = this.unit.chassisConfiguration && this.unit.chassisConfiguration.id;
          this.unit.bodyConfigurationId = this.unit.bodyConfiguration && this.unit.bodyConfiguration.id;
          this.unit.containerConfigurationId = this.unit.containerConfiguration && this.unit.containerConfiguration.id;
        },
        deep: true
      }
    }
  };
</script>
